import React, { useCallback, useMemo, useState, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// draft-js
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { convertToHTML } from "draft-convert";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

// sanitize potentially dangerous html
import DOMPurify from "dompurify";

// Custom styles for the MDEditor
import MDEditorRoot from "components/MDEditor/MDEditorRoot";

// Material Dashboard 3 PRO React context
import { useMaterialUIController } from "context";

const EMPTY_CONTENT = ContentState.createFromText("");

const createContentState = (rawHTML) => {
  if (!rawHTML) return EMPTY_CONTENT;

  try {
    const blocksFromHTML = convertFromHTML(DOMPurify.sanitize(rawHTML));
    if (!blocksFromHTML.contentBlocks) {
      return EMPTY_CONTENT;
    }
    return ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
  } catch (error) {
    return EMPTY_CONTENT;
  }
};

function MDEditor({ value = "", onChange }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const initialState = useMemo(
    () => EditorState.createWithContent(createContentState(value)),
    [value]
  );

  const [editorState, setEditorState] = useState(initialState);

  // keep internal state in sync if the caller provides a new value
  useEffect(() => {
    setEditorState(initialState);
  }, [initialState]);

  const handleEditorStateChange = useCallback(
    (nextState) => {
      setEditorState(nextState);

      if (typeof onChange === "function") {
        const html = convertToHTML(nextState.getCurrentContent());
        const sanitized = DOMPurify.sanitize(html);
        onChange(sanitized);
      }
    },
    [onChange]
  );

  const toolbarOptions = useMemo(
    () => ({
      options: [
        "inline",
        "blockType",
        "fontSize",
        "list",
        "textAlign",
        "colorPicker",
        "link",
        "image",
        "history",
      ],
      link: {
        defaultTargetOption: "_blank",
      },
      image: {
        uploadEnabled: false,
      },
    }),
    []
  );

  return (
    <MDEditorRoot ownerState={{ darkMode }}>
      <Editor
        toolbar={toolbarOptions}
        editorState={editorState}
        onEditorStateChange={handleEditorStateChange}
      />
    </MDEditorRoot>
  );
}

// Typechecking props for the MDEditor
MDEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default MDEditor;
